import type { ItemTreeSet } from '@/common/api/types/items';
import { createKeyedStore } from '@/common/utils/keyedStore';
import { allChangedIds, type TreeSetDiff } from './treeSetDiff';

/**
 * A reconciled tree-set fetched but not promoted, because it moved rows the user didn't touch.
 * `dismissed` collapses the offer to a persistent indicator — same data, less UI weight.
 */
export type HeldArrangement = {
  treeSet: ItemTreeSet;
  diff: TreeSetDiff;
  dismissed: boolean;
};

// clearOnUnsubscribe stays false so a remount can't discard a dismissed offer. Unbounded search-term
// keys are swept instead by itemsTreeSetConsistency.ts's startHeldArrangementCleanup.
const store = createKeyedStore<HeldArrangement>({ clearOnUnsubscribe: false });

export const getHeldArrangement = (queryKeyString: string): HeldArrangement | undefined =>
  store.get(queryKeyString);

export const subscribeHeldArrangement = (
  queryKeyString: string,
  listener: () => void,
): (() => void) => store.subscribe(queryKeyString, listener);

/** Overwrites rather than queues — a later reconciliation supersedes whatever was staged before. */
export const stageHeldArrangement = (
  queryKeyString: string,
  treeSet: ItemTreeSet,
  diff: TreeSetDiff,
): void => store.set(queryKeyString, { treeSet, diff, dismissed: false });

export const dismissHeldArrangement = (queryKeyString: string): void => {
  const current = store.get(queryKeyString);
  if (!current || current.dismissed) return;
  store.set(queryKeyString, { ...current, dismissed: true });
};

export const clearHeldArrangement = (queryKeyString: string): void => store.delete(queryKeyString);

/**
 * True if any held diff touches `itemIds`, regardless of whether a UI is subscribed — staging
 * happens as soon as classification runs, not when something renders the offer.
 */
export const isAnyIdHeld = (itemIds: readonly number[]): boolean => {
  if (itemIds.length === 0) return false;
  const ids = new Set(itemIds);
  for (const [, { diff }] of store.entries()) {
    if (allChangedIds(diff).some((id) => ids.has(id))) return true;
  }
  return false;
};
